import { useState, useEffect, useRef } from 'react';
import {
  useCreateIncidentObjective,
  useIncidentObjectives,
} from '../../hooks/incidentObjectiveHooks';
import { useIncidentStrategies } from '../../hooks/incidentStrategyHooks';
import { useIncidentTactics } from '../../hooks/incidentTacticHooks';
import { IapScope } from '../../ICSConstants';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewComboBox, {
  type ComboBoxOption,
} from 'components/DesignSystems/New/StylishNewComboBox';
import { OBJECTIVES_LIBRARY } from './ostLibraryData';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StrategyItem } from './WorkAnalysisItems/StrategyItem';
import { TacticItem } from './WorkAnalysisItems/TacticItem';
import { ObjectiveItem } from './WorkAnalysisItems/ObjectiveItem';
import { ScrollableList } from 'components/DesignSystems/ScrollableList';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { WorkLine } from './WorkAnalysisItems/WorkLine';
import { incidentCardWorkAnalysisMatrixTitle } from '../USCGPage';

type WorkAnalysisProps = {
  selectedTactics: string[];
  setSelectedTactics: React.Dispatch<React.SetStateAction<string[]>>;
  setTacticPoints: React.Dispatch<
    React.SetStateAction<{ x: number; y: number; id: string }[]>
  >;
  setEndScrollPoint: (point: { x: number; y: number } | null) => void;
  tacticPoints: { x: number; y: number; id: string }[];
  point2: { x: number; y: number } | null;
  endScrollPoint: { x: number; y: number } | null;
};

const WorkAnalysis: React.FC<WorkAnalysisProps> = ({
  selectedTactics,
  setSelectedTactics,
  setTacticPoints,
  setEndScrollPoint,
  tacticPoints,
  point2,
  endScrollPoint,
}) => {
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const scope = 'incident' as IapScope;
  const { data: incidentObjectives } = useIncidentObjectives(scope);
  const { data: incidentTactics } = useIncidentTactics(scope);
  const { data: incidentStrategies } = useIncidentStrategies(scope);

  const createObjective = useCreateIncidentObjective();
  const { exportAsForm } = useIcsFormPdfPreview();
  const observeRef = useRef<HTMLDivElement | null>(null);
  const endScrollRef = useRef<HTMLDivElement | null>(null);

  const handleExportICS234 = () => {
    void exportAsForm({
      formname: 'ICS_234_CG',
      data: {},
      mapSketch: null,
    });
  };
  const filteredObjectives = incidentObjectives?.filter((obj) => {
    if (obj.archived) return false;
    const type = obj.type || 'Operational';
    const isOperationalType = type === 'Operational' || type === 'OperationalAndManagerial';
    return isOperationalType && obj.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const strategyChildCount = incidentStrategies?.filter(
    (strat) => !strat.archived
  ).length;

  const tacticChildCount = incidentTactics?.filter(
    (tac) => !tac.archived
  ).length;

  const handleCreateObjective = async (name: string) => {
    await createObjective.mutateAsync({
      name: name,
      description: '',
      type: 'Operational',
    });
    setAdding(false);
  };

  const updateEndScrollLimit = () => {
    if (selectedTactics && endScrollRef.current) {
      const rect = endScrollRef.current.getBoundingClientRect();
      setEndScrollPoint({
        x: rect.left - 5,
        y: rect.top + rect.height / 4,
      });
    }
  };

  useEffect(() => {
    if (!endScrollRef.current) return;
    let lastX: number | null = null;
    let lastY: number | null = null;
    const interval = setInterval(() => {
      const rect = endScrollRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (rect.left !== lastX || rect.top !== lastY) {
        lastX = rect.left;
        lastY = rect.top;
        updateEndScrollLimit();
      }
    }, 20);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTactics]);

  return (
    <div className="w-100">
      <div>
        <div className="work-card-header" ref={observeRef}>
          {incidentCardWorkAnalysisMatrixTitle}
          <div className="d-flex gap-2">
            <StylishNewSearchBar
              onChangeSearchTerm={(term) => setSearchTerm(term)}
            />
            <StylishNewButton onClick={() => setAdding(!adding)}>
              <SharedIcon iconName="add" classes="me-1" size="sm" />
              Add Objective
            </StylishNewButton>
            <StylishNewButton
              onClick={handleExportICS234}
              className="button--primary"
            >
              Preview ICS-234
            </StylishNewButton>
          </div>
        </div>
        {adding && (
          <div className="bordered-card m-0 mb-2 d-flex align-items-center gap-2">
            <StylishNewComboBox
              classes="flex-grow-1"
              options={OBJECTIVES_LIBRARY}
              placeholder="Select or create Objective..."
              customOptionLabel="+ Create New"
              customInputMaxLength={200}
              onChange={(newValue) => {
                const option = newValue as ComboBoxOption | null;
                if (option) {
                  handleCreateObjective(option.label);
                }
              }}
            />
            <StylishNewButton
              className="button--icon primary"
              onClick={() => setAdding(false)}
            >
              <SharedIcon iconName="close" />
            </StylishNewButton>
          </div>
        )}
        <div style={{ zIndex: 2 }}>
          <ScrollableList hideScrollbar viewportHeightPercentage={80}>
            {filteredObjectives?.map((obj, index) => (
              <ObjectiveItem
                key={index}
                item={obj}
                selectedTactics={selectedTactics}
              >
                {incidentStrategies
                  ?.filter(
                    (strat) =>
                      strat.objectives.includes(obj.objective_id) &&
                      !strat.archived
                  )
                  ?.map((stra, index) => {
                    return (
                      <StrategyItem
                        key={index}
                        item={stra}
                        selectedTactics={selectedTactics}
                      >
                        {incidentTactics
                          ?.filter(
                            (tac) =>
                              tac.strategies.includes(stra.strategy_id) &&
                              !tac.archived
                          )
                          .map((tac, index) => (
                            <TacticItem
                              key={index}
                              item={tac}
                              setTacticPoints={setTacticPoints}
                              selectedTactics={selectedTactics}
                              setSelectedTactics={setSelectedTactics}
                            />
                          ))}
                      </StrategyItem>
                    );
                  })}
              </ObjectiveItem>
            ))}
          </ScrollableList>
        </div>
      </div>
      <WorkLine
        tacticPoints={tacticPoints}
        point2={point2}
        endScrollPoint={endScrollPoint}
      />
      <div
        className="work-card-footer d-flex justify-content-between"
        ref={endScrollRef}
      >
        <div>{filteredObjectives?.length} Objectives</div>
        <div>
          {strategyChildCount} Strategies {tacticChildCount} Tactics
        </div>
      </div>
    </div>
  );
};

export default WorkAnalysis;
